import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  generateMessages,
  analyzeMatch,
  generatePersonalizedOutreach,
  generateSmartOutreach,
  sendMessage,
  getTalentPool,
  getJobs
} from '../services/api';

function Outreach() {
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [messages, setMessages] = useState({ linkedin: [], email: [] });
  const [matchAnalysis, setMatchAnalysis] = useState(null);
  const [messageType, setMessageType] = useState('linkedin');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState('');
  const [sentimentData, setSentimentData] = useState(null);

  // Fetch candidates from talent pool and jobs on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [talentRes, jobsRes] = await Promise.all([
          getTalentPool(),
          getJobs()
        ]);

        // Handle talent pool response - returns { candidates: [], totalPages: ... }
        const talentData = talentRes.data;
        const candidatesList = Array.isArray(talentData) ? talentData :
                              Array.isArray(talentData?.candidates) ? talentData.candidates : [];

        // Normalize candidate data - talent pool uses 'fullName', ensure 'name' is also set
        const normalizedCandidates = candidatesList.map(c => ({
          ...c,
          name: c.name || c.fullName || 'Unknown'
        }));
        setCandidates(normalizedCandidates);

        // Handle jobs response - returns { jobs: [] }
        const jobsData = jobsRes.data;
        setJobs(
          Array.isArray(jobsData) ? jobsData :
          Array.isArray(jobsData?.jobs) ? jobsData.jobs :
          []
        );
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load candidates and jobs');
      }
    };
    fetchData();
  }, []);

  const handleAnalyzeMatch = async () => {
    if (!selectedCandidate || !selectedJob) {
      toast.error('Please select both a candidate and a job');
      return;
    }

    setIsLoading(true);
    setLoadingAction('analyze');
    try {
      const response = await analyzeMatch({
        candidate: selectedCandidate,
        jobDetails: selectedJob
      });

      const analysis = response.data.analysis || {};
      const processedAnalysis = {
        matchScore: analysis.matchScore || 50,
        overallMatch: analysis.overallMatch || 'Moderate',
        recommendation: analysis.recommendation || 'Moderate Match',
        matchReasons: Array.isArray(analysis.matchReasons) ? analysis.matchReasons :
                     (Array.isArray(analysis.analysis) ? analysis.analysis :
                     ['No specific match reasons available']),
        matchSummary: analysis.matchSummary || []
      };

      setMatchAnalysis(processedAnalysis);
      toast.success('Match analysis complete');
    } catch (error) {
      console.error('Error analyzing match:', error);
      toast.error('Failed to analyze candidate match');
      setMatchAnalysis({
        matchScore: 0,
        overallMatch: 'Error',
        recommendation: 'Error',
        matchReasons: ['An error occurred during analysis']
      });
    } finally {
      setIsLoading(false);
      setLoadingAction('');
    }
  };

  const handleGenerateMessages = async () => {
    if (!selectedCandidate || !selectedJob) {
      toast.error('Please select both a candidate and a job');
      return;
    }

    setIsLoading(true);
    setLoadingAction('generate');
    try {
      const response = await generateMessages({
        candidate: selectedCandidate,
        jobDetails: selectedJob,
        count: 3
      });

      const receivedMessages = response.data.messages || {};
      setMessages({
        linkedin: Array.isArray(receivedMessages.linkedin) ? receivedMessages.linkedin : [],
        email: Array.isArray(receivedMessages.email) ? receivedMessages.email : []
      });
      toast.success('Messages generated');
    } catch (error) {
      console.error('Error generating messages:', error);
      toast.error('Failed to generate messages');
    } finally {
      setIsLoading(false);
      setLoadingAction('');
    }
  };

  const handleGeneratePersonalizedOutreach = async () => {
    if (!selectedCandidate || !selectedJob) {
      toast.error('Please select both a candidate and a job');
      return;
    }

    setIsLoading(true);
    setLoadingAction('personalized');
    try {
      const response = await generatePersonalizedOutreach({
        candidate: selectedCandidate,
        jobDetails: selectedJob,
        platform: 'both',
        count: 2
      });

      const receivedMessages = response.data.messages || {};
      setMessages({
        linkedin: Array.isArray(receivedMessages.linkedin) ? receivedMessages.linkedin : [],
        email: Array.isArray(receivedMessages.email) ? receivedMessages.email : []
      });

      const analysis = response.data.analysis || {};
      setMatchAnalysis({
        matchScore: analysis.matchScore || 50,
        overallMatch: analysis.overallMatch || 'Moderate',
        recommendation: analysis.recommendation || 'Moderate Match',
        matchReasons: Array.isArray(analysis.matchReasons) ? analysis.matchReasons :
                     (Array.isArray(analysis.analysis) ? analysis.analysis :
                     ['No specific match reasons available'])
      });
      toast.success('Personalized outreach generated');
    } catch (error) {
      console.error('Error generating personalized outreach:', error);
      toast.error('Failed to generate personalized outreach');
      setMessages({ linkedin: [], email: [] });
    } finally {
      setIsLoading(false);
      setLoadingAction('');
    }
  };

  // New AI Smart Outreach handler
  const handleSmartOutreach = async () => {
    if (!selectedCandidate || !selectedJob) {
      toast.error('Please select both a candidate and a job');
      return;
    }

    // Need candidate and job IDs from database
    if (!selectedCandidate._id || !selectedJob._id) {
      toast.error('Please select candidates and jobs from the database');
      return;
    }

    setIsLoading(true);
    setLoadingAction('smart');
    try {
      const response = await generateSmartOutreach({
        candidateId: selectedCandidate._id,
        jobId: selectedJob._id,
        channel: 'both',
        count: 2
      });

      const data = response.data;

      // Set messages
      const receivedMessages = data.messages || {};
      setMessages({
        linkedin: Array.isArray(receivedMessages.linkedin) ? receivedMessages.linkedin : [],
        email: Array.isArray(receivedMessages.email) ? receivedMessages.email : []
      });

      // Set match analysis
      if (data.analysis) {
        setMatchAnalysis({
          matchScore: data.analysis.matchScore || 50,
          overallMatch: data.analysis.recommendation?.includes('Strong') ? 'Strong' :
                       data.analysis.recommendation?.includes('Weak') ? 'Weak' : 'Moderate',
          recommendation: data.analysis.recommendation || 'Moderate Match',
          matchReasons: data.analysis.matchSummary || ['Match analysis completed'],
          matchSummary: data.analysis.matchSummary || []
        });
      }

      // Set sentiment data if available
      if (data.metadata?.sentiment) {
        setSentimentData(data.metadata.sentiment);
      }

      toast.success('AI Smart Outreach generated!');
    } catch (error) {
      console.error('Error generating smart outreach:', error);
      toast.error('Failed to generate smart outreach');
      setMessages({ linkedin: [], email: [] });
    } finally {
      setIsLoading(false);
      setLoadingAction('');
    }
  };

  const handleSendMessage = async (message, channel = 'email') => {
    // Determine the channel from message object or parameter
    const messageChannel = (typeof message === 'object' && message.channel) || channel;

    // Check if candidate has email for email channel
    if (messageChannel === 'email' && !selectedCandidate?.email) {
      toast.error('Candidate does not have an email address. Please copy the message instead.');
      return;
    }

    // For LinkedIn messages, we can't auto-send - copy to clipboard instead
    if (messageChannel === 'linkedin') {
      const textToCopy = typeof message === 'object' ? message.message : message;
      navigator.clipboard.writeText(textToCopy);
      toast.info('LinkedIn messages cannot be sent automatically. Message copied to clipboard - please paste it in LinkedIn.');

      // If candidate has LinkedIn URL, offer to open it
      if (selectedCandidate?.linkedinUrl || selectedCandidate?.linkedin) {
        const linkedinUrl = selectedCandidate.linkedinUrl || selectedCandidate.linkedin;
        window.open(linkedinUrl, '_blank');
      }
      return;
    }

    try {
      // Prepare email data
      const messageObj = typeof message === 'object' ? message : { message: message };

      const response = await sendMessage({
        candidateId: selectedCandidate._id || selectedCandidate.id,
        candidate: selectedCandidate,
        message: {
          ...messageObj,
          channel: 'email'
        }
      });

      if (response.data.success) {
        toast.success(`Email sent successfully to ${selectedCandidate.email}`);
      } else if (response.data.error) {
        toast.error(response.data.error);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = error.response?.data?.error || 'Failed to send email';
      toast.error(errorMessage);
    }
  };

  const copyToClipboard = (message) => {
    const textToCopy = typeof message === 'object'
      ? (message.subject ? `Subject: ${message.subject}\n\n${message.message}` : message.message)
      : message;
    navigator.clipboard.writeText(textToCopy);
    toast.success('Message copied to clipboard');
  };

  const getMatchScoreColor = (score) => {
    if (score >= 70) return 'bg-green-100 text-green-800 border-green-200';
    if (score >= 40) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  const getSentimentBadge = (sentiment) => {
    if (!sentiment) return null;
    const colors = {
      positive: 'bg-green-100 text-green-700',
      neutral: 'bg-gray-100 text-gray-700',
      negative: 'bg-red-100 text-red-700'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[sentiment.sentiment] || colors.neutral}`}>
        {sentiment.sentiment} ({Math.round((sentiment.positiveScore || 0) * 100)}% positive)
      </span>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Candidate Outreach</h1>
        <span className="text-sm text-gray-500">
          {candidates.length} candidates | {jobs.length} jobs loaded
        </span>
      </div>

      {/* Selection Form */}
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="candidate" className="block text-sm font-medium text-gray-700">
                Select Candidate
              </label>
              <select
                id="candidate"
                value={selectedCandidate?._id || selectedCandidate?.id || ''}
                onChange={(e) => {
                  const candidate = candidates.find(c => (c._id || c.id) === e.target.value);
                  setSelectedCandidate(candidate);
                  setMatchAnalysis(null);
                  setMessages({ linkedin: [], email: [] });
                  setSentimentData(null);
                }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                <option value="">Select a candidate</option>
                {candidates.map((candidate) => (
                  <option key={candidate._id || candidate.id} value={candidate._id || candidate.id}>
                    {candidate.fullName || candidate.name} {candidate.domain ? `(${candidate.domain})` : ''}
                  </option>
                ))}
              </select>
              {selectedCandidate && (
                <div className="mt-2 text-xs text-gray-500">
                  <p><strong>Skills:</strong> {selectedCandidate.skills?.slice(0, 5).join(', ') || 'N/A'}</p>
                  {selectedCandidate.domain && <p><strong>Domain:</strong> {selectedCandidate.domain}</p>}
                  {selectedCandidate.email && <p><strong>Email:</strong> {selectedCandidate.email}</p>}
                </div>
              )}
            </div>

            <div>
              <label htmlFor="job" className="block text-sm font-medium text-gray-700">
                Select Job
              </label>
              <select
                id="job"
                value={selectedJob?._id || selectedJob?.id || ''}
                onChange={(e) => {
                  const job = jobs.find(j => (j._id || j.id) === e.target.value);
                  setSelectedJob(job);
                  setMatchAnalysis(null);
                  setMessages({ linkedin: [], email: [] });
                  setSentimentData(null);
                }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                <option value="">Select a job</option>
                {jobs.map((job) => (
                  <option key={job._id || job.id} value={job._id || job.id}>
                    {job.title} {job.company ? `@ ${job.company}` : ''}
                  </option>
                ))}
              </select>
              {selectedJob && (
                <div className="mt-2 text-xs text-gray-500">
                  <p><strong>Location:</strong> {selectedJob.location || 'Remote'}</p>
                  {selectedJob.industry && <p><strong>Industry:</strong> {selectedJob.industry}</p>}
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleAnalyzeMatch}
              disabled={isLoading || !selectedCandidate || !selectedJob}
              className="inline-flex items-center rounded-md border border-transparent bg-gray-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {isLoading && loadingAction === 'analyze' ? 'Analyzing...' : 'Analyze Match'}
            </button>

            <button
              type="button"
              onClick={handleGenerateMessages}
              disabled={isLoading || !selectedCandidate || !selectedJob}
              className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {isLoading && loadingAction === 'generate' ? 'Generating...' : 'Generate Messages'}
            </button>

            <button
              type="button"
              onClick={handleGeneratePersonalizedOutreach}
              disabled={isLoading || !selectedCandidate || !selectedJob}
              className="inline-flex items-center rounded-md border border-transparent bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {isLoading && loadingAction === 'personalized' ? 'Generating...' : 'Personalized Outreach'}
            </button>

            <button
              type="button"
              onClick={handleSmartOutreach}
              disabled={isLoading || !selectedCandidate?._id || !selectedJob?._id}
              className="inline-flex items-center rounded-md border border-transparent bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
              title={!selectedCandidate?._id || !selectedJob?._id ? 'Requires database candidates/jobs' : 'AI-powered outreach with match analysis'}
            >
              {isLoading && loadingAction === 'smart' ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <span className="mr-1">✨</span> AI Smart Outreach
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Match Analysis */}
      {matchAnalysis && (
        <div className="mt-8 bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Match Analysis</h2>
              {sentimentData && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500">Message Sentiment:</span>
                  {getSentimentBadge(sentimentData.linkedin || sentimentData.email)}
                </div>
              )}
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center gap-4 mb-4">
                <div className={`px-4 py-2 rounded-lg border ${getMatchScoreColor(matchAnalysis.matchScore)}`}>
                  <span className="text-2xl font-bold">{matchAnalysis.matchScore}%</span>
                  <span className="ml-2 text-sm">{matchAnalysis.recommendation}</span>
                </div>
              </div>
              <div>
                <h3 className="font-medium mb-2">Why They Match:</h3>
                <ul className="space-y-2">
                  {(matchAnalysis.matchSummary?.length > 0 ? matchAnalysis.matchSummary : matchAnalysis.matchReasons)?.map((reason, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      <span className="text-gray-700">{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Message Type Selector */}
      {((messages.linkedin && messages.linkedin.length > 0) || (messages.email && messages.email.length > 0)) && (
        <div className="mt-8 mb-4">
          <div className="flex space-x-4 border-b">
            <button
              onClick={() => setMessageType('linkedin')}
              className={`py-2 px-4 font-medium flex items-center gap-2 ${
                messageType === 'linkedin'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
              </svg>
              LinkedIn ({messages.linkedin?.length || 0})
            </button>
            <button
              onClick={() => setMessageType('email')}
              className={`py-2 px-4 font-medium flex items-center gap-2 ${
                messageType === 'email'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Email ({messages.email?.length || 0})
            </button>
          </div>
        </div>
      )}

      {/* Generated Messages */}
      <div className="mt-4 space-y-4">
        {messageType === 'linkedin' && messages.linkedin && messages.linkedin.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              LinkedIn Messages
              <span className="text-xs font-normal px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                Conversational Tone
              </span>
            </h2>
            {messages.linkedin.map((message, index) => {
              const msgContent = typeof message === 'object' ? message.message : message;
              const msgTone = typeof message === 'object' ? message.tone : null;

              return (
                <div key={index} className="bg-white p-4 rounded-lg shadow mb-4 border-l-4 border-blue-500">
                  {msgTone && (
                    <div className="mb-2">
                      <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded">
                        {msgTone}
                      </span>
                    </div>
                  )}
                  <p className="text-gray-800 whitespace-pre-wrap">{msgContent}</p>
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => copyToClipboard(message)}
                      className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 text-sm"
                    >
                      Copy
                    </button>
                    <button
                      onClick={() => handleSendMessage(message, 'linkedin')}
                      className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 text-sm flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                      </svg>
                      Open LinkedIn
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {messageType === 'email' && messages.email && messages.email.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              Email Messages
              <span className="text-xs font-normal px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                Professional Tone
              </span>
            </h2>
            {messages.email.map((message, index) => {
              const msgSubject = typeof message === 'object' ? message.subject : null;
              const msgContent = typeof message === 'object' ? message.message : message;
              const msgTone = typeof message === 'object' ? message.tone : null;

              return (
                <div key={index} className="bg-white p-4 rounded-lg shadow mb-4 border-l-4 border-purple-500">
                  {msgSubject && (
                    <div className="mb-3 pb-3 border-b border-gray-200">
                      <span className="text-sm text-gray-500">Subject:</span>
                      <p className="font-medium text-gray-900">{msgSubject}</p>
                    </div>
                  )}
                  {msgTone && (
                    <div className="mb-2">
                      <span className="text-xs px-2 py-0.5 bg-purple-50 text-purple-600 rounded">
                        {msgTone}
                      </span>
                    </div>
                  )}
                  <p className="text-gray-800 whitespace-pre-wrap">{msgContent}</p>
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => copyToClipboard(message)}
                      className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 text-sm"
                    >
                      Copy
                    </button>
                    <button
                      onClick={() => handleSendMessage(message, 'email')}
                      disabled={!selectedCandidate?.email}
                      className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 text-sm flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={!selectedCandidate?.email ? 'Candidate has no email address' : `Send email to ${selectedCandidate.email}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      {selectedCandidate?.email ? 'Send Email' : 'No Email'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Empty State */}
      {messages.linkedin?.length === 0 && messages.email?.length === 0 && !isLoading && selectedCandidate && selectedJob && (
        <div className="mt-8 text-center py-12 bg-gray-50 rounded-lg">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No messages yet</h3>
          <p className="mt-1 text-sm text-gray-500">
            Click "AI Smart Outreach" to generate personalized messages with match analysis.
          </p>
        </div>
      )}
    </div>
  );
}

export default Outreach;
